import type { ComponentProps, ReactNode } from "react";

import { Link } from "@/i18n/navigation";

type LinkHref = ComponentProps<typeof Link>["href"];

// `pb-0.5`: the mono face sits low in its line box; two pixels under lift the
// label one, to the middle of the pill.
const pill =
  "group relative inline-flex h-12 shrink-0 items-center whitespace-nowrap rounded-full bg-fg px-6 pb-0.5 font-mono text-sm text-bg cursor-pointer hover:opacity-80 transition-opacity disabled:bg-transparent disabled:text-fg-muted disabled:border disabled:border-fg-subtle disabled:cursor-not-allowed focus-visible:outline-none focus-visible:no-underline";

function Corners() {
  // The brackets sit at the corners of the *bounding box*, outside the
  // rounded-full pill itself — i.e. on the page background, not on the
  // pill. So fg (white in dark mode, black in light mode) contrasts
  // cleanly against the page in both themes.
  const corner =
    "absolute leading-none text-fg-subtle transition-colors group-focus-visible:text-fg";
  return (
    <>
      <span aria-hidden className={`${corner} top-0 right-0`}>
        ⌝
      </span>
      <span aria-hidden className={`${corner} bottom-0 left-0`}>
        ⌞
      </span>
    </>
  );
}

/**
 * The primary pill button — solid fg fill with the ⌝⌞ corner-bracket
 * frame. Four modes:
 *   - `href` set (no `external`) → next-intl locale-aware <Link>
 *   - `href` set + `external` true → plain <a target="_blank">
 *     (use for `mailto:`, `tel:`, and off-site URLs)
 *   - `onClick` set → in-page action <button type="button">
 *   - none → form submit <button type="submit">
 */
type CtaButtonProps = {
  children: ReactNode;
} & (
  | {
      href: LinkHref;
      external?: false;
      type?: never;
      disabled?: never;
      onClick?: never;
      busy?: never;
    }
  | {
      href: string;
      external: true;
      type?: never;
      disabled?: never;
      onClick?: never;
      busy?: never;
    }
  | {
      href?: never;
      external?: never;
      type?: "button" | "submit";
      disabled?: boolean;
      onClick?: () => void;
      /** Request in flight — shows the shared rainbow loading ring (and fades
       *  the fill out, since this is a solid pill). For submit/action buttons. */
      busy?: boolean;
    }
);

export function CtaButton({ children, ...props }: CtaButtonProps) {
  if ("href" in props && props.href) {
    if (props.external) {
      return (
        <a href={props.href} target="_blank" rel="noreferrer" className={pill}>
          <Corners />
          {children}
        </a>
      );
    }
    return (
      <Link href={props.href} className={pill}>
        <Corners />
        {children}
      </Link>
    );
  }
  return (
    <button
      type={props.type ?? "button"}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-busy={props.busy || undefined}
      className={`${pill} rainbow-ring rainbow-ring-solid${
        props.busy ? " rainbow-ring-busy cursor-progress" : ""
      }`}
    >
      <Corners />
      {children}
    </button>
  );
}
